import { ref } from "valtio";

import type { AdsConfig } from "@/feature-ads/data-model-ads-static.mjs";
import { proxy } from "@/util/proxy.mjs";

interface AdsRefs {
  shouldShowAds: (() => boolean)[];
  BLACKLISTED_PATHS: string[];
  waitForAdsConfirmation: () => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  internalRefresh(slots: any[]): void;
  ADHESION_PATHS: string[];
  SCRIPTS: AsRef<Record<PropertyKey, string>>;
  vendorSetup: AsRef<Record<string, () => void>>;
  googleInit(): void;
  initStates: Record<string, Promise<void>>;
  googleCmd(fn: () => void): Promise<void>;
  targetingHandler(key: string, value: string, slot: string): void;
  remoteConfig: AsRef<Partial<AdsConfig>>;
  registerAd(ad);
  destroyAds(ads);
  canRefresh(): boolean;
  showIngameVideo: boolean;
  showProfileVideo: boolean;
  useOutstream: boolean;
  showInFeedBanner: boolean;
  INFEED_BANNER_PATHS: string[];
  showHeaderBanner: boolean;
  EXCLUDED_HEADER_BANNER_PATHS: string[];
  showInContentBanner: boolean;
  IN_CONTENT_BANNER_PATHS: string[];
}

const adsRefs = proxy<AdsRefs>({
  /** An array of handlers to determine if ads should be shown. Will be visible if and only if none of the handlers return `false`. */
  shouldShowAds: [],

  /* Blacklisted paths that we remotely maintain to make google happy */
  BLACKLISTED_PATHS: [],

  // This is just a promise that should resolve as soon as we can confirm.
  waitForAdsConfirmation: () => Promise.resolve(),

  ADHESION_PATHS: [
    // LOL
    "/lol/tierlist",
    "/lol/champions/overview",
    "/lol/champions/synergies",
    // DEV
    // "/dev/video-ad-test",
    "", // ENABLES FOR ALL PAGES
  ],

  // Overwrite this externally.
  async internalRefresh(_slots) {},

  SCRIPTS: ref<Record<PropertyKey, string>>({}),

  vendorSetup: ref<ArbitraryMap<() => void>>({}),

  googleInit() {},
  initStates: {},
  async googleCmd(_fn) {},

  targetingHandler(_key, _value, _slot) {},

  remoteConfig: ref<Partial<AdsConfig>>({}),
  registerAd(_ad) {},
  destroyAds(_ads) {},
  canRefresh: () => true,
  showIngameVideo: true,
  showProfileVideo: false,
  useOutstream: false,
  showInFeedBanner: false,
  INFEED_BANNER_PATHS: ["/profile/"],
  showHeaderBanner: false,
  EXCLUDED_HEADER_BANNER_PATHS: [
    "/profile/",
    "/lol/champions/overview",
    "/lol/champions/synergies",
    "/lol/in-game",
    "/lol/tierlist",
  ],
  showInContentBanner: false,
  IN_CONTENT_BANNER_PATHS: [
    "/apex/match/",
    "/cs2/match/",
    "/eft/match/",
    "/fortnite/match/",
    "/lol/match/",
    "/tft/match/",
    "/valorant/match/",
    "/cs2/in-game",
    "/eft/in-game",
    "/fortnite/in-game",
    "/tft/in-game",
    "/valorant/in-game",
  ],
});

adsRefs.vendorSetup = ref<ArbitraryMap<() => void>>(
  new Proxy(
    {},
    {
      get(target, key) {
        const entry = Object.entries(adsRefs.SCRIPTS ?? {}).find(
          ([, v]) => v === key,
        );
        if (!entry) return target[key];
        return target[entry[0]];
      },
    },
  ),
);

export default adsRefs;
